import { createSlice } from '@reduxjs/toolkit';
import { isEqual } from 'lodash';
import initialState from './initialState';
import {
  getExclusivityProperties,
  getExpiringRentProperties,
  getOverviewConstructionAge,
  getOverviewContactsSource,
  getOverviewDevelopmentsLocations,
  getOverviewDevelopmentsTypes,
  getOverviewEntryDate,
  getOverviewStaleProperties,
  getOverviewStaleContacts,
  getOverviewLeads,
  getOverviewOperationStatistics,
  getOverviewPendingEvents,
  getOverviewPropertiesLocations,
  getOverviewPropertiesOperations,
  getOverviewPropertiesTypes,
  getOverviewSummary,
  getOverviewVisitEvents,
  getPendingContacts,
  getPendingEvents,
  getPendingLeads,
  getPendingReservations,
  getPerformanceAddedPropertiesCard,
  getPerformanceAddedPropertiesMiniCard,
  getPerformanceClosedLeadsCard,
  getPerformanceConcretedOperationsCard,
  getPerformanceConcretedOperationsMiniCard,
  getPerformanceConversionRateCard,
  getPerformanceConversionRateMiniCard,
  getPerformanceDoneEventsMiniCard,
  getPerformanceDoneVisitsCard,
  getPerformanceMedianResponseTimeCard,
  getPerformanceConversionTimeMiniCard,
  getPerformanceNewContactsCard,
  getPerformanceNewContactsMiniCard,
  getPerformanceProductionRateCard,
  getPerformanceReceivedLeadsCard,
  getPerformanceReceivedLeadsMiniCard,
  getPerformanceReservationCard,
  getPerformanceResponseTimeCard,
  getPerformanceResponseTimeMiniCard,
  getPerformanceVisitEventsCard,
  getPortalsNotifications,
  getUnassignedInquiries,
  getVisitsToConfirm,
  getPropertiesPendingReview,
  getDevelopmentsPendingReview,
  getQprecoMarketplace,
  getMarketplaceRegistrationQuality,
  getPerformanceConversionTimeCard,
  getPerformancePropertyConsultationsCard,
  getPerformanceDevelopmentConsultationsCard,
  getPerformanceNewContactsTable,
  getPerformanceInquiriesReceivedTable,
  getPerformanceDoneVisitsTable,
  getPerformanceResponseTimeTable,
  getPerformanceAddedPropertiesTable,
  getPerformanceConversionTimeTable,
  getPerformanceConcretedOperationsTable,
  getPerformanceConversionRateTable,
  getPerformancePropertyConsultationsTable,
  getPerformanceDevelopmentConsultationsTable,
  getPerformanceClosedLeadsTable,
  getPerformanceReservationsCreatedTable,
  getPerformanceAgentsPerformanceTable,
  getPerformanceProductionRateTable,
  getPerformanceConcretedOperationsEvolutionCard,
  getPerformanceConcretedOperationsSourceCard,
  putDashboardConfig,
  getPerformanceDoneEventsCard,
  getPerformanceDoneEventsTable,
  getOverviewTasks,
  editOverviewTask,
} from './service';
import {
  CLOSED_LEADS,
  DONE_EVENTS,
  DONE_VISITS,
  INQUIRY_CATEGORIES,
  PENDING_REVIEW_VIEW,
  PROPERTIES_STATUS,
  PROPERTY_ADDED_CATEGORIES,
  PROPERTY_OPERATIONS,
  TASKS_OPTIONS,
} from '@/components/Dashboard/constants';
import getArrowIcon from '@/components/Dashboard/utils/getArrowIcon';
import Trans from '@/components/shared/Trans';
import translations from '@/components/Dashboard/Dashboard.trans.json';
import {
  buildBackendStructureTabs,
  buildFrontendStructureTabs,
  reconcileStructures,
} from '@/components/Dashboard/utils/dashboardConfig';
import DEFAULT_SECTIONS from '@/components/Dashboard/constants/defaultSections';
import {
  getNormalizedOverviewTasks,
  getOverviewTasksWithoutTask,
  markOverviewTaskCompleting,
  resetOverviewTaskCompleting,
} from '@/components/Dashboard/utils/overviewTasks';
import reorderSectionsByVisibility from '@/components/Dashboard/utils/reorderSectionsByVisibility';
import { buildStaleRecordsChartData } from '@/components/Dashboard/utils/staleRecordsChartData';
import { COMPLETED_TASK_STATUS } from '@/components/Tasks/constants/status';

const findSectionIndexById = (sections, sectionId) =>
  (sections ?? []).findIndex((section) => section?.id === sectionId);

const PENDING_REVIEW_VIEWS = Object.values(PENDING_REVIEW_VIEW);

const setPendingReviewLoading = (cardState) => {
  cardState.loading = true;
  cardState.hasFetched = false;
  cardState.error = null;
  PENDING_REVIEW_VIEWS.forEach((view) => {
    cardState[view].empty = null;
  });
};

const setPendingReviewData = (cardState, payload) => {
  cardState.loading = false;
  cardState.hasFetched = true;
  cardState.error = null;
  cardState.redirectUrl = payload?.redirect_url ?? null;
  PENDING_REVIEW_VIEWS.forEach((view) => {
    const bucket = payload?.[view] ?? { count: 0, items: [] };
    cardState[view].count = bucket.count;
    cardState[view].items = bucket.items;
    cardState[view].empty = Array.isArray(bucket.items)
      ? bucket.items.length === 0
      : false;
  });
};

const setPendingReviewError = (cardState) => {
  cardState.loading = false;
  cardState.hasFetched = true;
  cardState.error = true;
  cardState.redirectUrl = null;
  PENDING_REVIEW_VIEWS.forEach((view) => {
    cardState[view].count = 0;
    cardState[view].items = [];
    cardState[view].empty = true;
  });
};

export const DashboardSlice = createSlice({
  name: 'Dashboard',
  initialState,
  reducers: {
    setTabActive: (state, action) => {
      state.tabActive = action.payload;
    },
    setMiniCards: (state, action) => {
      const { key, value } = action.payload;
      state.miniCards[key] = value;

      if (key === 'selectedStatusId') {
        const propertiesMap = {
          [PROPERTIES_STATUS.APPRAISAL]:
            state.miniCards.allProperties.to_be_cotized,
          [PROPERTIES_STATUS.AVAILABLE]:
            state.miniCards.allProperties.available,
          [PROPERTIES_STATUS.RESERVED]: state.miniCards.allProperties.reserved,
        };

        state.miniCards.propertiesValue = propertiesMap[value];
      }
    },
    setOverviewOperationStatistics(state, action) {
      const { key, value } = action.payload;
      state.overviewOperationStatistics[key] = value;
    },
    setPropertiesTypes(state, action) {
      const { key, value } = action.payload;
      state.overviewPropertiesTypes[key] = value;
    },
    setPropertiesLocations(state, action) {
      const { key, value } = action.payload;
      state.overviewPropertiesLocations[key] = value;
    },
    setConstructionAge(state, action) {
      const { key, value } = action.payload;
      state.overviewConstructionAge[key] = value;
    },
    setEntryDate(state, action) {
      const { key, value } = action.payload;
      state.overviewEntryDate[key] = value;
    },
    setQprecoMarketplace(state, action) {
      const { key, value } = action.payload;
      state.qprecoMarketplace[key] = value;
    },
    setInquiriesReceived(state, action) {
      const { key, value } = action.payload;
      state.inquiriesReceived[key] = value;
    },
    setAddedProperties(state, action) {
      const { key, value } = action.payload;
      state.addedProperties[key] = value;
    },
    setOverviewTasks(state, action) {
      const { key, value } = action.payload;
      state.overviewTasks[key] = value;
    },
    setConcretedOperations(state, action) {
      const { key, value } = action.payload;
      state.concretedOperations[key] = value;
    },
    setClosedLeads(state, action) {
      const { key, value } = action.payload;
      state.closedLeads[key] = value;
    },
    setConcretedOperationsSource(state, action) {
      const { key, value } = action.payload;
      state.concretedOperationsSource[key] = value;
    },
    increaseRefreshCount(state) {
      state.refreshCount += 1;
    },
    setPerformanceModals(state, action) {
      const { key, subkey, value } = action.payload;

      if (subkey === 'isOpen') {
        DashboardSlice.caseReducers.togglePerformanceModal(state, {
          type: 'Dashboard/togglePerformanceModal',
          payload: { key, value },
        });
        return;
      }

      if (subkey === 'sortBy') {
        DashboardSlice.caseReducers.setPerformanceModalSort(state, {
          type: 'Dashboard/setPerformanceModalSort',
          payload: { key, value },
        });
        return;
      }

      if (subkey === 'groupByBranch') {
        DashboardSlice.caseReducers.setPerformanceModalGroupByBranch(state, {
          type: 'Dashboard/setPerformanceModalGroupByBranch',
          payload: { key, value },
        });
        return;
      }

      if (subkey === 'searchType') {
        DashboardSlice.caseReducers.setPerformanceModalSearchType(state, {
          type: 'Dashboard/setPerformanceModalSearchType',
          payload: { key, value },
        });
        return;
      }

      if (subkey) {
        state.performanceModals[key][subkey] = value;
      } else {
        state.performanceModals[key] = value;
      }
    },
    togglePerformanceModal(state, action) {
      const { key, value } = action.payload;
      const isGoingToCloseIt = !value;

      if (isGoingToCloseIt) {
        state.performanceModals[key] = initialState.performanceModals[key];
      } else {
        state.performanceModals[key].isOpen = true;
      }
    },
    setPerformanceModalSort(state, action) {
      const { key, value } = action.payload;

      let newSortDirection = 'asc';

      if (state.performanceModals[key].sortBy === value) {
        newSortDirection =
          state.performanceModals[key].sortDirection === 'asc' ? 'desc' : 'asc';
      }

      state.performanceModals[key].sortBy = value;
      state.performanceModals[key].sortDirection = newSortDirection;
    },
    setPerformanceModalGroupByBranch(state, action) {
      const { key, value } = action.payload;

      state.performanceModals[key].lastGroupByBranch =
        state.performanceModals[key].groupByBranch;

      state.performanceModals[key].groupByBranch = value;
    },
    setPerformanceModalSearchType(state, action) {
      const { key, value } = action.payload;

      state.performanceModals[key].searchType = value;

      if (state.performanceModals[key].searchQuery) {
        state.performanceModals[key].searchTypeFlag += 1;
      }
    },
    clearPerformanceModalFilters(state, action) {
      const { modal, filters } = action.payload;

      filters.forEach((filter) => {
        state.performanceModals[modal][filter] =
          initialState.performanceModals[modal][filter];
      });
    },
    setModalPendingState(state, action) {
      const { modal, tableQuery } = action.payload;

      state.performanceModals[modal].loading = true;
      state.performanceModals[modal].hasFetched = false;

      const params = new URLSearchParams(tableQuery ?? '');
      const pageRaw = params.get('page');
      const page = pageRaw ? Number(pageRaw) : 1;

      const { groupByBranch, lastGroupByBranch } =
        state.performanceModals[modal];

      const modeChanged = lastGroupByBranch !== groupByBranch;
      const shouldReset = page <= 1 || modeChanged;

      if (shouldReset) {
        state.performanceModals[modal].list = [];
        state.performanceModals[modal].branches = [];
        return;
      }

      if (groupByBranch) {
        state.performanceModals[modal].list =
          state.performanceModals[modal].list ?? [];
      } else {
        state.performanceModals[modal].branches =
          state.performanceModals[modal].branches ?? [];
      }
    },
    setModalFulfilledState(state, action) {
      const {
        modal,
        page_info,
        results = [],
        extraConfig = {},
        ...rest
      } = action.payload;

      const { skipPageUpdate = false, replaceResults = false } = extraConfig;

      const modalState = state.performanceModals[modal];

      const page = page_info?.page ?? 0;
      const has_next = page_info?.has_next ?? false;
      const count = page_info?.total ?? 0;
      const per_page = page_info?.per_page ?? 0;
      const num_pages = page_info?.num_pages ?? 0;

      const dataKey = modalState.groupByBranch ? 'branches' : 'list';
      const prevValues = modalState[dataKey] ?? [];

      const nextValues = replaceResults ? results : prevValues.concat(results);

      const update = {
        ...modalState,
        ...rest,
        loading: false,
        hasFetched: true,
        has_next,
        count,
        per_page,
        num_pages,
        [dataKey]: nextValues,
      };

      if (!skipPageUpdate) {
        update.page = page;
      }

      state.performanceModals[modal] = update;
    },
    setEdition(state, action) {
      const { key, subkey, value } = action.payload;

      if (subkey) {
        state.edition[key][subkey] = value;
        return;
      }

      state.edition[key] = value;
    },
    setInitialEditionData(state, action) {
      const safeStructure = reconcileStructures(
        action.payload,
        DEFAULT_SECTIONS
      );
      const frontendObj = buildFrontendStructureTabs(safeStructure);

      state.edition.loading = false;
      state.edition.tabs = frontendObj;
    },
    addEntryToTrack(state, action) {
      const { entriesToTrack } = state.edition;
      const entryExists = entriesToTrack.some((entry) =>
        isEqual(entry, action.payload)
      );

      state.edition.entriesToTrack = entryExists
        ? entriesToTrack.filter((entry) => isEqual(entry, action.payload))
        : [...entriesToTrack, action.payload];
    },
    toggleEditionCardVisibility(state, action) {
      const getSortedSectionCards = (section, cardId) => {
        const maxOrder = section.cards.reduce(
          (acc, card) => Math.max(acc, card.order ?? 0),
          0
        );

        const updatedCards = section.cards.map((card) =>
          card.id !== cardId
            ? card
            : {
                ...card,
                visible: !card.visible,
                expanded: Boolean(card.alwaysExpanded),
                order: card.visible ? maxOrder + 1 : card.order,
              }
        );
        const normalizedCards = [...updatedCards]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((card, index) => ({ ...card, order: index }));

        return normalizedCards;
      };
      const { tabId, sectionId, cardId } = action.payload;
      const sections = state.edition.tabs?.[tabId]?.sections;
      const sectionIndex = findSectionIndexById(sections, sectionId);

      if (!sections || sectionIndex < 0) return;

      const section = sections[sectionIndex];

      const sortedSectionCards = getSortedSectionCards(section, cardId);
      const titleVisible = sortedSectionCards.some((card) => card.visible);

      sections[sectionIndex] = {
        ...section,
        cards: sortedSectionCards,
        visible: titleVisible,
        dirty: true,
      };

      const eventToTrack =
        sectionId === 'mini_cards'
          ? 'platform_dashboard_custom_minicards'
          : 'platform_dashboard_custom_cards';
      const elementType = sectionId === 'mini_cards' ? 'minicard' : 'card';
      DashboardSlice.caseReducers.addEntryToTrack(state, {
        type: 'Dashboard/addEntryToTrack',
        payload: {
          card: cardId,
          tab: tabId,
          type: elementType,
          event: eventToTrack,
        },
      });
    },
    toggleEditionTitleVisibility(state, action) {
      const { tabId, sectionId } = action.payload;
      const sections = state.edition.tabs?.[tabId]?.sections;
      const sectionIndex = findSectionIndexById(sections, sectionId);

      if (!sections || sectionIndex < 0) return;

      const section = sections[sectionIndex];
      const newTitleIsVisible = !section.visible === true;

      const updatedCards = section.cards.map((card) => {
        const newVisibleValue = newTitleIsVisible;
        const newExpandedValue = Boolean(card.alwaysExpanded);

        if (newVisibleValue !== card.visible) {
          const eventToTrack =
            sectionId === 'mini_cards'
              ? 'platform_dashboard_custom_minicards'
              : 'platform_dashboard_custom_cards';
          const elementType = sectionId === 'mini_cards' ? 'minicard' : 'card';
          DashboardSlice.caseReducers.addEntryToTrack(state, {
            type: 'Dashboard/addEntryToTrack',
            payload: {
              card: card.id,
              tab: tabId,
              type: elementType,
              event: eventToTrack,
            },
          });
        }

        if (newExpandedValue !== card.expanded) {
          const eventToTrack = 'platform_dashboard_custom_cards_expand';
          const elementType = sectionId === 'mini_cards' ? 'minicard' : 'card';
          DashboardSlice.caseReducers.addEntryToTrack(state, {
            type: 'Dashboard/addEntryToTrack',
            payload: {
              card: card.id,
              tab: tabId,
              type: elementType,
              event: eventToTrack,
            },
          });
        }

        return {
          ...card,
          visible: newVisibleValue,
          expanded: newExpandedValue,
        };
      });

      const updatedSection = {
        ...section,
        visible: newTitleIsVisible,
        cards: updatedCards,
        dirty: true,
      };
      const nextSections = sections.map((sectionItem, index) =>
        index === sectionIndex ? updatedSection : sectionItem
      );
      state.edition.tabs[tabId].sections = reorderSectionsByVisibility(
        nextSections,
        sectionId,
        newTitleIsVisible
      );
    },
    toggleEditionCardExpand(state, action) {
      const { tabId, sectionId, cardId } = action.payload;
      const sections = state.edition.tabs?.[tabId]?.sections;
      const sectionIndex = findSectionIndexById(sections, sectionId);

      if (!sections || sectionIndex < 0) return;

      const section = sections[sectionIndex];

      const updatedCards = section.cards.map((card) =>
        card.id !== cardId
          ? card
          : {
              ...card,
              expanded: card.alwaysExpanded ? true : !card.expanded,
            }
      );

      sections[sectionIndex] = {
        ...section,
        cards: updatedCards,
        dirty: true,
      };

      const eventToTrack = 'platform_dashboard_custom_cards_expand';
      const elementType = sectionId === 'mini_cards' ? 'minicard' : 'card';
      DashboardSlice.caseReducers.addEntryToTrack(state, {
        type: 'Dashboard/addEntryToTrack',
        payload: {
          card: cardId,
          tab: tabId,
          type: elementType,
          event: eventToTrack,
        },
      });
    },
    moveEditionSection(state, action) {
      const { tabId, sectionId, direction } = action.payload;
      const tab = state.edition.tabs?.[tabId];
      const sections = tab?.sections;

      const orderedSections = [...sections].sort((a, b) => a.order - b.order);

      const currentIndex = orderedSections.findIndex(
        (section) => section.id === sectionId
      );
      const nextIndex =
        direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

      if (
        !sections ||
        currentIndex < 0 ||
        nextIndex < 0 ||
        nextIndex >= orderedSections.length
      ) {
        return;
      }

      const currSection = orderedSections[currentIndex];
      const nextSection = orderedSections[nextIndex];

      const updatedSections = sections.map((section) => {
        if (section.id === currSection.id) {
          return {
            ...section,
            order: nextSection.order,
            dirty: true,
          };
        }

        if (section.id === nextSection.id) {
          return {
            ...section,
            order: currSection.order,
            dirty: true,
          };
        }

        return section;
      });

      tab.sections = updatedSections;
    },
    reorderEditionCards(state, action) {
      const { tabId, sectionId, orderedIds } = action.payload;
      const sections = state.edition.tabs?.[tabId]?.sections;
      const sectionIndex = findSectionIndexById(sections, sectionId);
      const section = sections?.[sectionIndex];

      if (!section) return;

      const getOrderedCardsAfterDnd = () => {
        const cards = section.cards || [];
        const cardsById = new Map(cards.map((card) => [card.id, card]));
        const orderedSet = new Set(orderedIds);
        const orderedCards = orderedIds
          .map((id) => cardsById.get(id))
          .filter(Boolean);
        const remainingCards = cards
          .filter((card) => !orderedSet.has(card.id))
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const sectionCards = [...orderedCards, ...remainingCards].map(
          (card, index) => ({
            ...card,
            order: index,
          })
        );

        return sectionCards;
      };

      section.cards = getOrderedCardsAfterDnd();
      section.dirty = true;
    },
    toggleActiveLegend(state, action) {
      const { activeLegends } = state.concretedOperationsEvolution;
      const targetLegend = action.payload;

      const updatedActiveLegends = activeLegends.includes(targetLegend)
        ? activeLegends.filter((legend) => legend !== targetLegend)
        : [...activeLegends, targetLegend];

      state.concretedOperationsEvolution.activeLegends = updatedActiveLegends;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getPendingEvents.pending, (state) => {
      state.events.loading = true;
      state.events.hasFetched = false;
      state.events.error = null;
      state.events.empty = null;
    });
    builder.addCase(getPendingEvents.fulfilled, (state, action) => {
      const { count_today, next_event_time, has_now, events } = action.payload;

      state.events.loading = false;

      state.events.hasFetched = true;
      state.events.error = null;
      state.events.empty = Array.isArray(events) ? events.length === 0 : false;
      state.events.countToday = count_today;
      state.events.nextEventTime = next_event_time;
      state.events.hasNow = has_now;
      state.events.items = events;
    });
    builder.addCase(getPendingEvents.rejected, (state) => {
      state.events.loading = false;
      state.events.error = true;
    });
    builder.addCase(getUnassignedInquiries.pending, (state) => {
      state.unassignedInquiries.loading = true;
      state.unassignedInquiries.hasFetched = false;
      state.unassignedInquiries.error = null;
      state.unassignedInquiries.empty = null;
    });
    builder.addCase(getUnassignedInquiries.fulfilled, (state, action) => {
      const { count, inquiries } = action.payload;

      state.unassignedInquiries.loading = false;

      state.unassignedInquiries.hasFetched = true;
      state.unassignedInquiries.error = null;
      state.unassignedInquiries.empty = Array.isArray(inquiries)
        ? inquiries.length === 0
        : false;
      state.unassignedInquiries.count = count;
      state.unassignedInquiries.inquiries = inquiries;
    });
    builder.addCase(getUnassignedInquiries.rejected, (state) => {
      state.unassignedInquiries.loading = false;
      state.unassignedInquiries.error = true;
    });
    builder.addCase(getVisitsToConfirm.pending, (state) => {
      state.visitsToConfirm.loading = true;
      state.visitsToConfirm.hasFetched = false;
      state.visitsToConfirm.error = null;
      state.visitsToConfirm.empty = null;
    });
    builder.addCase(getVisitsToConfirm.fulfilled, (state, action) => {
      const { count, visits } = action.payload;

      state.visitsToConfirm.loading = false;
      state.visitsToConfirm.hasFetched = true;
      state.visitsToConfirm.error = null;
      state.visitsToConfirm.empty = Array.isArray(visits)
        ? visits.length === 0
        : false;
      state.visitsToConfirm.count = count;
      state.visitsToConfirm.visits = visits;
    });
    builder.addCase(getVisitsToConfirm.rejected, (state) => {
      state.visitsToConfirm.loading = false;
      state.visitsToConfirm.error = true;
    });
    builder.addCase(getPropertiesPendingReview.pending, (state) => {
      setPendingReviewLoading(state.propertiesPendingReview);
    });
    builder.addCase(getPropertiesPendingReview.fulfilled, (state, action) => {
      setPendingReviewData(state.propertiesPendingReview, action.payload);
    });
    builder.addCase(getPropertiesPendingReview.rejected, (state) => {
      setPendingReviewError(state.propertiesPendingReview);
    });
    builder.addCase(getDevelopmentsPendingReview.pending, (state) => {
      setPendingReviewLoading(state.developmentsPendingReview);
    });
    builder.addCase(getDevelopmentsPendingReview.fulfilled, (state, action) => {
      setPendingReviewData(state.developmentsPendingReview, action.payload);
    });
    builder.addCase(getDevelopmentsPendingReview.rejected, (state) => {
      setPendingReviewError(state.developmentsPendingReview);
    });
    builder.addCase(getQprecoMarketplace.pending, (state) => {
      state.qprecoMarketplace.loading = true;
      state.qprecoMarketplace.hasFetched = false;
      state.qprecoMarketplace.error = null;
      state.qprecoMarketplace.empty = null;
    });
    builder.addCase(getQprecoMarketplace.fulfilled, (state, action) => {
      const payload = action.payload ?? {};

      state.qprecoMarketplace.loading = false;
      state.qprecoMarketplace.hasFetched = true;
      state.qprecoMarketplace.error = null;
      state.qprecoMarketplace.enabled = payload.enabled ?? false;
      state.qprecoMarketplace.operations = {
        sale: payload.sale ?? null,
        rent: payload.rent ?? null,
        ...(Object.prototype.hasOwnProperty.call(payload, 'temporary')
          ? { temporary: payload.temporary }
          : {}),
      };
      state.qprecoMarketplace.empty = false;
      if (
        !Object.prototype.hasOwnProperty.call(
          state.qprecoMarketplace.operations,
          state.qprecoMarketplace.selectedOperation
        )
      ) {
        state.qprecoMarketplace.selectedOperation = PROPERTY_OPERATIONS.SALE;
      }
    });
    builder.addCase(getQprecoMarketplace.rejected, (state) => {
      state.qprecoMarketplace.loading = false;
      state.qprecoMarketplace.error = true;
    });
    builder.addCase(getMarketplaceRegistrationQuality.pending, (state) => {
      state.marketplaceRegistrationQuality.loading = true;
      state.marketplaceRegistrationQuality.hasFetched = false;
      state.marketplaceRegistrationQuality.error = null;
      state.marketplaceRegistrationQuality.empty = null;
    });
    builder.addCase(
      getMarketplaceRegistrationQuality.fulfilled,
      (state, action) => {
        const payload = action.payload ?? {};
        const levels = payload.levels ?? [];

        state.marketplaceRegistrationQuality.loading = false;
        state.marketplaceRegistrationQuality.hasFetched = true;
        state.marketplaceRegistrationQuality.error = null;
        state.marketplaceRegistrationQuality.enabled = payload.enabled ?? false;
        state.marketplaceRegistrationQuality.redirectUrl =
          payload.redirect_url ?? null;
        state.marketplaceRegistrationQuality.count =
          payload.total_count ?? null;
        state.marketplaceRegistrationQuality.levels = levels;
        state.marketplaceRegistrationQuality.empty = false;
      }
    );
    builder.addCase(getMarketplaceRegistrationQuality.rejected, (state) => {
      state.marketplaceRegistrationQuality.loading = false;
      state.marketplaceRegistrationQuality.error = true;
    });
    builder.addCase(getPendingContacts.pending, (state) => {
      state.pendingContacts.loading = true;
      state.pendingContacts.hasFetched = false;
      state.pendingContacts.error = null;
      state.pendingContacts.empty = null;
    });
    builder.addCase(getPendingContacts.fulfilled, (state, action) => {
      const { count, contacts } = action.payload;

      state.pendingContacts.loading = false;

      state.pendingContacts.hasFetched = true;
      state.pendingContacts.error = null;
      state.pendingContacts.empty = contacts.length === 0;
      state.pendingContacts.count = count;
      state.pendingContacts.contacts = contacts;
    });
    builder.addCase(getPendingContacts.rejected, (state) => {
      state.pendingContacts.loading = false;
      state.pendingContacts.error = true;
    });
    builder.addCase(getPendingLeads.pending, (state) => {
      state.pendingLeads.loading = true;
      state.pendingLeads.hasFetched = false;
      state.pendingLeads.error = null;
      state.pendingLeads.empty = null;
    });
    builder.addCase(getPendingLeads.fulfilled, (state, action) => {
      const { count, opportunities } = action.payload;

      state.pendingLeads.loading = false;

      state.pendingLeads.hasFetched = true;
      state.pendingLeads.error = null;
      state.pendingLeads.empty = opportunities.length === 0;
      state.pendingLeads.count = count;
      state.pendingLeads.leads = opportunities;
    });
    builder.addCase(getPendingLeads.rejected, (state) => {
      state.pendingLeads.loading = false;
      state.pendingLeads.error = true;
    });
    builder.addCase(getPendingReservations.pending, (state) => {
      state.pendingReservations.loading = true;
      state.pendingReservations.hasFetched = false;
      state.pendingReservations.error = null;
      state.pendingReservations.empty = null;
    });
    builder.addCase(getPendingReservations.fulfilled, (state, action) => {
      const { count, reservations } = action.payload;

      state.pendingReservations.loading = false;

      state.pendingReservations.hasFetched = true;
      state.pendingReservations.error = null;
      state.pendingReservations.empty = reservations.length === 0;
      state.pendingReservations.count = count;
      state.pendingReservations.reservations = reservations;
    });
    builder.addCase(getPendingReservations.rejected, (state) => {
      state.pendingReservations.loading = false;
      state.pendingReservations.error = true;
    });
    builder.addCase(getPortalsNotifications.pending, (state) => {
      state.portalsNotifications.loading = true;
      state.portalsNotifications.hasFetched = false;
      state.portalsNotifications.error = null;
      state.portalsNotifications.empty = null;
    });
    builder.addCase(getPortalsNotifications.fulfilled, (state, action) => {
      const { total_alerts, total_errors, properties, portals } =
        action.payload;

      state.portalsNotifications.loading = false;

      state.portalsNotifications.hasFetched = true;
      state.portalsNotifications.error = null;
      state.portalsNotifications.total_alerts = total_alerts;
      state.portalsNotifications.total_errors = total_errors;
      if (properties) {
        state.portalsNotifications.properties = properties;
        state.portalsNotifications.portals = [];
        state.portalsNotifications.empty = properties.length === 0;
      }
      if (portals) {
        state.portalsNotifications.portals = portals;
        state.portalsNotifications.properties = [];
        state.portalsNotifications.empty = portals.length === 0;
      }
    });
    builder.addCase(getPortalsNotifications.rejected, (state) => {
      state.portalsNotifications.loading = false;
      state.portalsNotifications.error = true;
    });
    builder.addCase(getExclusivityProperties.pending, (state) => {
      state.exclusiveProperties.loading = true;
      state.exclusiveProperties.hasFetched = false;
      state.exclusiveProperties.error = null;
      state.exclusiveProperties.empty = null;
    });
    builder.addCase(getExclusivityProperties.fulfilled, (state, action) => {
      const { count, properties } = action.payload;

      state.exclusiveProperties.loading = false;

      state.exclusiveProperties.hasFetched = true;
      state.exclusiveProperties.error = null;
      state.exclusiveProperties.empty = properties.length === 0;
      state.exclusiveProperties.count = count;
      state.exclusiveProperties.properties = properties;
    });
    builder.addCase(getExclusivityProperties.rejected, (state) => {
      state.exclusiveProperties.loading = false;
      state.exclusiveProperties.error = true;
    });
    builder.addCase(getExpiringRentProperties.pending, (state) => {
      state.expiringRentProperties.loading = true;
      state.expiringRentProperties.hasFetched = false;
      state.expiringRentProperties.error = null;
      state.expiringRentProperties.empty = null;
    });
    builder.addCase(getExpiringRentProperties.fulfilled, (state, action) => {
      const { count, properties } = action.payload;

      state.expiringRentProperties.loading = false;

      state.expiringRentProperties.hasFetched = true;
      state.expiringRentProperties.error = null;
      state.expiringRentProperties.empty = properties.length === 0;
      state.expiringRentProperties.count = count;
      state.expiringRentProperties.properties = properties;
    });
    builder.addCase(getExpiringRentProperties.rejected, (state) => {
      state.expiringRentProperties.loading = false;
      state.expiringRentProperties.error = true;
    });

    builder.addCase(getOverviewSummary.pending, (state) => {
      state.miniCards.loading = true;
      state.miniCards.hasFetched = false;
      state.miniCards.error = false;
    });
    builder.addCase(getOverviewSummary.fulfilled, (state, action) => {
      const { active_contacts, properties_by_status, available_developments } =
        action.payload;

      const propertiesMap = {
        [PROPERTIES_STATUS.APPRAISAL]: String(
          properties_by_status.to_be_cotized
        ),
        [PROPERTIES_STATUS.AVAILABLE]: String(properties_by_status.available),
        [PROPERTIES_STATUS.RESERVED]: String(properties_by_status.reserved),
      };

      state.miniCards.loading = false;

      state.miniCards.hasFetched = true;
      state.miniCards.contactsValue = String(active_contacts);
      state.miniCards.propertiesValue =
        propertiesMap[state.miniCards.selectedStatusId];
      state.miniCards.developmentsValue = String(available_developments);
      state.miniCards.allProperties = properties_by_status;
    });
    builder.addCase(getOverviewSummary.rejected, (state) => {
      state.miniCards.loading = false;
      state.miniCards.error = true;
    });
    builder.addCase(getOverviewContactsSource.pending, (state) => {
      state.contactSource.loading = true;
      state.contactSource.hasFetched = false;
      state.contactSource.error = false;
      state.contactSource.empty = false;
    });
    builder.addCase(getOverviewContactsSource.fulfilled, (state, action) => {
      const { list, others, others_contacts_count } = action.payload;

      state.contactSource.loading = false;

      state.contactSource.hasFetched = true;
      state.contactSource.error = false;
      state.contactSource.empty = list.length === 0;
      state.contactSource.list = list;
      state.contactSource.others = others;
      state.contactSource.others_contacts_count = others_contacts_count;
    });
    builder.addCase(getOverviewContactsSource.rejected, (state) => {
      state.contactSource.loading = false;
      state.contactSource.error = true;
    });

    builder.addCase(getOverviewLeads.pending, (state) => {
      state.overviewLeads.loading = true;
      state.overviewLeads.hasFetched = false;
      state.overviewLeads.error = false;
      state.overviewLeads.empty = false;
    });
    builder.addCase(getOverviewLeads.fulfilled, (state, action) => {
      const { list, others } = action.payload;

      state.overviewLeads.loading = false;

      state.overviewLeads.hasFetched = true;
      state.overviewLeads.error = false;
      state.overviewLeads.empty = list.length === 0;
      state.overviewLeads.list = list;
      state.overviewLeads.others = others;
    });
    builder.addCase(getOverviewLeads.rejected, (state) => {
      state.overviewLeads.loading = false;
      state.overviewLeads.error = true;
    });

    builder.addCase(getOverviewPendingEvents.pending, (state) => {
      state.pendingScheduledEvents.loading = true;
      state.pendingScheduledEvents.hasFetched = false;
      state.pendingScheduledEvents.error = false;
      state.pendingScheduledEvents.empty = false;
    });
    builder.addCase(getOverviewPendingEvents.fulfilled, (state, action) => {
      const { list, others } = action.payload.event_types;

      state.pendingScheduledEvents.loading = false;

      state.pendingScheduledEvents.hasFetched = true;
      state.pendingScheduledEvents.error = false;
      state.pendingScheduledEvents.empty = list.length === 0;
      state.pendingScheduledEvents.list = list;
      state.pendingScheduledEvents.others = others;
    });
    builder.addCase(getOverviewPendingEvents.rejected, (state) => {
      state.pendingScheduledEvents.loading = false;
      state.pendingScheduledEvents.error = true;
    });

    builder.addCase(getOverviewOperationStatistics.pending, (state) => {
      state.overviewOperationStatistics.loading = true;
      state.overviewOperationStatistics.hasFetched = false;
      state.overviewOperationStatistics.error = false;
    });
    builder.addCase(
      getOverviewOperationStatistics.fulfilled,
      (state, action) => {
        const { sale, rent, currencies } = action.payload;

        const selectedOperation = PROPERTY_OPERATIONS.SALE;
        const selectedCurrency = currencies[0];

        state.overviewOperationStatistics.loading = false;

        state.overviewOperationStatistics.hasFetched = true;
        state.overviewOperationStatistics.error = false;
        state.overviewOperationStatistics.currencies = currencies;
        state.overviewOperationStatistics.sale = sale;
        state.overviewOperationStatistics.rent = rent;
        state.overviewOperationStatistics.selectedOperation = selectedOperation;
        state.overviewOperationStatistics.selectedCurrency = selectedCurrency;
      }
    );
    builder.addCase(getOverviewOperationStatistics.rejected, (state) => {
      state.overviewOperationStatistics.loading = false;
      state.overviewOperationStatistics.error = true;
    });

    builder.addCase(getOverviewVisitEvents.pending, (state) => {
      state.showings.loading = true;
      state.showings.hasFetched = false;
      state.showings.error = false;
      state.showings.empty = false;
    });
    builder.addCase(getOverviewVisitEvents.fulfilled, (state, action) => {
      const { labels, datasets, event_type_ids } = action.payload;

      state.showings.loading = false;

      state.showings.hasFetched = true;
      state.showings.error = false;
      state.showings.empty = event_type_ids.length === 0;
      state.showings.event_type_ids = event_type_ids;
      state.showings.datasets = datasets;
      state.showings.labels = labels.map((label) => ({
        monthId: label.monthId,
        name: label.label,
      }));
    });
    builder.addCase(getOverviewVisitEvents.rejected, (state) => {
      state.showings.loading = false;
      state.showings.error = true;
    });

    builder.addCase(getOverviewPropertiesOperations.pending, (state) => {
      state.overviewPropertiesOperations.loading = true;
      state.overviewPropertiesOperations.hasFetched = false;
      state.overviewPropertiesOperations.error = false;
    });
    builder.addCase(
      getOverviewPropertiesOperations.fulfilled,
      (state, action) => {
        const { operations } = action.payload;

        const colorMap = {
          1: '#F16D6E', // sale
          2: '#427F94', // rent
          3: '#B9DCE7', // temporary rent
        };

        const labels = operations.map((operation) => ({
          operationId: operation.type,
          name: operation.name,
        }));

        const datasets = [
          {
            label: Trans(translations, 'Operations'),
            data: operations.map((operation) => operation.total),
            backgroundColor: operations.map(
              (operation) => colorMap[operation.type]
            ),
          },
        ];

        state.overviewPropertiesOperations.loading = false;

        state.overviewPropertiesOperations.hasFetched = true;
        state.overviewPropertiesOperations.error = false;
        state.overviewPropertiesOperations.datasets = datasets;
        state.overviewPropertiesOperations.labels = labels;
      }
    );
    builder.addCase(getOverviewPropertiesOperations.rejected, (state) => {
      state.overviewPropertiesOperations.loading = false;
      state.overviewPropertiesOperations.error = true;
    });

    builder.addCase(getOverviewPropertiesTypes.pending, (state) => {
      state.overviewPropertiesTypes.loading = true;
      state.overviewPropertiesTypes.hasFetched = false;
      state.overviewPropertiesTypes.error = false;
    });
    builder.addCase(getOverviewPropertiesTypes.fulfilled, (state, action) => {
      const { list, others } = action.payload.property_types;

      const colors = ['#7B0C0D', '#ED3D3E', '#F16D6E', '#9AC1D0', '#427F94'];

      const labels = list.map((item) => ({
        typeId: item.type_id,
        name: item.name,
        count: item.count,
        isOthers: false,
      }));

      const data = list.map((item) => item.count);
      const backgroundColors = list.map((_, index) => colors[index]);

      if (others?.length > 0) {
        const ids = others.map((item) => item.type_id);
        const othersCount = others.reduce((acc, curr) => acc + curr.count, 0);

        labels.push({
          typeId: ids,
          name: 'others',
          count: othersCount,
          isOthers: true,
          othersDetail: others,
        });
        data.push(othersCount);
        backgroundColors.push(colors[list.length] || '#9AC1D0');
      }

      const datasets = [
        {
          label: 'PropertiesType',
          data,
          backgroundColor: backgroundColors,
        },
      ];

      state.overviewPropertiesTypes.loading = false;

      state.overviewPropertiesTypes.hasFetched = true;
      state.overviewPropertiesTypes.error = false;
      state.overviewPropertiesTypes.labels = labels;
      state.overviewPropertiesTypes.datasets = datasets;
    });
    builder.addCase(getOverviewPropertiesTypes.rejected, (state) => {
      state.overviewPropertiesTypes.loading = false;
      state.overviewPropertiesTypes.error = true;
    });

    builder.addCase(getOverviewPropertiesLocations.pending, (state) => {
      state.overviewPropertiesLocations.loading = true;
      state.overviewPropertiesLocations.hasFetched = false;
      state.overviewPropertiesLocations.error = false;
    });
    builder.addCase(
      getOverviewPropertiesLocations.fulfilled,
      (state, action) => {
        const { list, others } = action.payload.property_locations;

        const colors = ['#7B0C0D', '#ED3D3E', '#F16D6E', '#9AC1D0', '#427F94'];

        const labels = list.map((item) => ({
          locationId: item.location_id,
          name: item.name,
          count: item.count,
          isOthers: false,
        }));

        const data = list.map((item) => item.count);
        const backgroundColors = list.map((_, index) => colors[index]);

        if (others?.length > 0) {
          const ids = others.map((item) => item.location_id);
          const propertiesCount = others.reduce(
            (acc, curr) => acc + curr.count,
            0
          );
          const locationsCount = others.length;

          labels.push({
            locationId: ids,
            name: 'others',
            count: propertiesCount,
            isOthers: true,
            othersDetail: others,
            locationsCount,
          });
          data.push(propertiesCount);
          backgroundColors.push(colors[list.length] || '#9AC1D0');
        }

        const datasets = [
          {
            label: 'PropertiesLocation',
            data,
            backgroundColor: backgroundColors,
          },
        ];

        state.overviewPropertiesLocations.loading = false;

        state.overviewPropertiesLocations.hasFetched = true;
        state.overviewPropertiesLocations.error = false;
        state.overviewPropertiesLocations.labels = labels;
        state.overviewPropertiesLocations.datasets = datasets;
      }
    );
    builder.addCase(getOverviewPropertiesLocations.rejected, (state) => {
      state.overviewPropertiesLocations.loading = false;
      state.overviewPropertiesLocations.error = true;
    });

    builder.addCase(getOverviewConstructionAge.pending, (state) => {
      state.overviewConstructionAge.loading = true;
      state.overviewConstructionAge.hasFetched = false;
      state.overviewConstructionAge.error = false;
    });
    builder.addCase(getOverviewConstructionAge.fulfilled, (state, action) => {
      const { age_buckets, theme } = action.payload;
      const backgroundColor = theme === 'univen' ? '#4F6D98' : '#78A6B8';

      const labels = age_buckets.map((bucket, index) => ({
        ageId: index,
        name: bucket.name,
        count: bucket.count,
        min: bucket.min,
        max: bucket.max,
      }));

      const data = age_buckets.map((bucket) => bucket.count);

      const datasets = [
        {
          label: Trans(translations, 'ConstructionAge'),
          data,
          backgroundColor,
        },
      ];

      state.overviewConstructionAge.loading = false;

      state.overviewConstructionAge.hasFetched = true;
      state.overviewConstructionAge.error = false;
      state.overviewConstructionAge.labels = labels;
      state.overviewConstructionAge.datasets = datasets;
    });
    builder.addCase(getOverviewConstructionAge.rejected, (state) => {
      state.overviewConstructionAge.loading = false;
      state.overviewConstructionAge.error = true;
    });

    builder.addCase(getOverviewEntryDate.pending, (state) => {
      state.overviewEntryDate.loading = true;
      state.overviewEntryDate.hasFetched = false;
      state.overviewEntryDate.error = false;
    });
    builder.addCase(getOverviewEntryDate.fulfilled, (state, action) => {
      const { availability_date_buckets, theme } = action.payload;
      const backgroundColor = theme === 'univen' ? '#4F6D98' : '#78A6B8';

      const labels = availability_date_buckets.map((bucket, index) => ({
        dateId: index,
        name: bucket.name,
        count: bucket.count,
        range: bucket.range,
      }));

      const data = availability_date_buckets.map((bucket) => bucket.count);

      const datasets = [
        {
          label: Trans(translations, 'EntryDate'),
          data,
          backgroundColor,
        },
      ];

      state.overviewEntryDate.loading = false;

      state.overviewEntryDate.hasFetched = true;
      state.overviewEntryDate.error = false;
      state.overviewEntryDate.labels = labels;
      state.overviewEntryDate.datasets = datasets;
    });
    builder.addCase(getOverviewEntryDate.rejected, (state) => {
      state.overviewEntryDate.loading = false;
      state.overviewEntryDate.error = true;
    });

    builder.addCase(getOverviewStaleProperties.pending, (state) => {
      state.overviewStaleProperties.loading = true;
      state.overviewStaleProperties.hasFetched = false;
      state.overviewStaleProperties.error = false;
    });
    builder.addCase(getOverviewStaleProperties.fulfilled, (state, action) => {
      const { theme, ...bucketsResponse } = action.payload;
      const backgroundColor = theme === 'univen' ? '#4F6D98' : '#78A6B8';

      Object.assign(
        state.overviewStaleProperties,
        buildStaleRecordsChartData(
          bucketsResponse,
          'PropertiesLastUpdate',
          backgroundColor
        )
      );

      state.overviewStaleProperties.loading = false;
      state.overviewStaleProperties.hasFetched = true;
      state.overviewStaleProperties.error = false;
    });
    builder.addCase(getOverviewStaleProperties.rejected, (state) => {
      state.overviewStaleProperties.loading = false;
      state.overviewStaleProperties.error = true;
    });

    builder.addCase(getOverviewStaleContacts.pending, (state) => {
      state.overviewStaleContacts.loading = true;
      state.overviewStaleContacts.hasFetched = false;
      state.overviewStaleContacts.error = false;
    });
    builder.addCase(getOverviewStaleContacts.fulfilled, (state, action) => {
      const { theme, ...bucketsResponse } = action.payload;
      const backgroundColor = theme === 'univen' ? '#4F6D98' : '#78A6B8';

      Object.assign(
        state.overviewStaleContacts,
        buildStaleRecordsChartData(
          bucketsResponse,
          'ContactsLastUpdate',
          backgroundColor
        )
      );

      state.overviewStaleContacts.loading = false;
      state.overviewStaleContacts.hasFetched = true;
      state.overviewStaleContacts.error = false;
    });
    builder.addCase(getOverviewStaleContacts.rejected, (state) => {
      state.overviewStaleContacts.loading = false;
      state.overviewStaleContacts.error = true;
    });

    builder.addCase(getOverviewDevelopmentsTypes.pending, (state) => {
      state.overviewDevelopmentsTypes.loading = true;
      state.overviewDevelopmentsTypes.hasFetched = false;
      state.overviewDevelopmentsTypes.error = false;
    });
    builder.addCase(getOverviewDevelopmentsTypes.fulfilled, (state, action) => {
      const { list, others } = action.payload.development_types;

      const colors = ['#7B0C0D', '#ED3D3E', '#F16D6E', '#9AC1D0', '#427F94'];

      const labels = list.map((item) => ({
        typeId: item.type_id,
        name: item.name,
        count: item.count,
        isOthers: false,
      }));

      const data = list.map((item) => item.count);
      const backgroundColors = list.map((_, index) => colors[index]);

      if (others?.length > 0) {
        const ids = others.map((item) => item.type_id);
        const othersCount = others.reduce((acc, curr) => acc + curr.count, 0);

        labels.push({
          typeId: ids,
          name: 'others',
          count: othersCount,
          isOthers: true,
          othersDetail: others,
          nonClickable: true,
        });
        data.push(othersCount);
        backgroundColors.push(colors[list.length] || '#9AC1D0');
      }

      const datasets = [
        {
          label: 'DevelopmentsType',
          data,
          backgroundColor: backgroundColors,
        },
      ];

      state.overviewDevelopmentsTypes.loading = false;

      state.overviewDevelopmentsTypes.hasFetched = true;
      state.overviewDevelopmentsTypes.error = false;
      state.overviewDevelopmentsTypes.labels = labels;
      state.overviewDevelopmentsTypes.datasets = datasets;
    });
    builder.addCase(getOverviewDevelopmentsTypes.rejected, (state) => {
      state.overviewDevelopmentsTypes.loading = false;
      state.overviewDevelopmentsTypes.error = true;
    });

    builder.addCase(getOverviewDevelopmentsLocations.pending, (state) => {
      state.overviewDevelopmentsLocations.loading = true;
      state.overviewDevelopmentsLocations.hasFetched = false;
      state.overviewDevelopmentsLocations.error = false;
    });
    builder.addCase(
      getOverviewDevelopmentsLocations.fulfilled,
      (state, action) => {
        const { list, others } = action.payload.development_locations;

        const colors = ['#7B0C0D', '#ED3D3E', '#F16D6E', '#9AC1D0', '#427F94'];

        const labels = list.map((item) => ({
          locationId: item.location_id,
          name: item.name,
          count: item.count,
          isOthers: false,
        }));

        const data = list.map((item) => item.count);
        const backgroundColors = list.map((_, index) => colors[index]);

        if (others?.length > 0) {
          const ids = others.map((item) => item.location_id);
          const othersCount = others.reduce((acc, curr) => acc + curr.count, 0);
          const locationsCount = others.length;

          labels.push({
            locationId: ids,
            name: 'others',
            count: othersCount,
            isOthers: true,
            othersDetail: others,
            locationsCount,
            nonClickable: true,
          });
          data.push(othersCount);
          backgroundColors.push(colors[list.length] || '#9AC1D0');
        }

        const datasets = [
          {
            label: 'DevelopmentsLocation',
            data,
            backgroundColor: backgroundColors,
          },
        ];

        state.overviewDevelopmentsLocations.loading = false;

        state.overviewDevelopmentsLocations.hasFetched = true;
        state.overviewDevelopmentsLocations.error = false;
        state.overviewDevelopmentsLocations.labels = labels;
        state.overviewDevelopmentsLocations.datasets = datasets;
      }
    );
    builder.addCase(getOverviewDevelopmentsLocations.rejected, (state) => {
      state.overviewDevelopmentsLocations.loading = false;
      state.overviewDevelopmentsLocations.error = true;
    });

    // getOverviewTasks
    builder.addCase(getOverviewTasks.pending, (state) => {
      state.overviewTasks.loading = true;
      state.overviewTasks.hasFetched = false;
      state.overviewTasks.error = false;
    });
    builder.addCase(getOverviewTasks.fulfilled, (state, action) => {
      const { tasks, counts } = getNormalizedOverviewTasks(action.payload);

      state.overviewTasks.tasks = tasks;
      state.overviewTasks.counts = counts;
      state.overviewTasks.loading = false;
      state.overviewTasks.hasFetched = true;
      state.overviewTasks.error = false;
    });
    builder.addCase(getOverviewTasks.rejected, (state) => {
      state.overviewTasks.loading = false;
      state.overviewTasks.error = true;
    });

    // editOverviewTask
    builder.addCase(editOverviewTask.pending, (state, action) => {
      const { id: taskId, payload } = action.meta.arg ?? {};
      const isCompleting = payload?.status === COMPLETED_TASK_STATUS;

      if (!isCompleting || !taskId) return;

      state.overviewTasks.tasks[TASKS_OPTIONS.TODAY] =
        markOverviewTaskCompleting(
          state.overviewTasks.tasks[TASKS_OPTIONS.TODAY],
          taskId
        );
      state.overviewTasks.tasks[TASKS_OPTIONS.EXPIRED] =
        markOverviewTaskCompleting(
          state.overviewTasks.tasks[TASKS_OPTIONS.EXPIRED],
          taskId
        );
    });
    builder.addCase(editOverviewTask.fulfilled, (state, action) => {
      const { id: taskId, payload } = action.meta.arg ?? {};
      const isCompleting = payload?.status === COMPLETED_TASK_STATUS;

      state.overviewTasks.loading = false;
      state.overviewTasks.error = false;
      state.overviewTasks.overviewTasksRefreshCount += 1;

      if (!isCompleting || !taskId) return;

      const { tasks, counts } = getOverviewTasksWithoutTask(
        state.overviewTasks,
        taskId
      );

      state.overviewTasks.tasks = tasks;
      state.overviewTasks.counts = counts;
    });
    builder.addCase(editOverviewTask.rejected, (state, action) => {
      const { id: taskId, payload } = action.meta.arg ?? {};
      const isCompleting = payload?.status === COMPLETED_TASK_STATUS;

      state.overviewTasks.loading = false;
      state.overviewTasks.error = true;

      if (!isCompleting || !taskId) return;

      state.overviewTasks.tasks[TASKS_OPTIONS.TODAY] =
        resetOverviewTaskCompleting(
          state.overviewTasks.tasks[TASKS_OPTIONS.TODAY],
          taskId
        );
      state.overviewTasks.tasks[TASKS_OPTIONS.EXPIRED] =
        resetOverviewTaskCompleting(
          state.overviewTasks.tasks[TASKS_OPTIONS.EXPIRED],
          taskId
        );
    });

    builder.addCase(getPerformanceReceivedLeadsCard.pending, (state) => {
      state.inquiriesReceived.loading = true;
      state.inquiriesReceived.hasFetched = false;
      state.inquiriesReceived.error = false;
    });
    builder.addCase(
      getPerformanceReceivedLeadsCard.fulfilled,
      (state, action) => {
        const {
          count,
          variation,
          percentage_status,
          operation_breakdown,
          origin_breakdown,
        } = action.payload;

        const { top, others } = origin_breakdown;

        state.inquiriesReceived.loading = false;

        state.inquiriesReceived.hasFetched = true;
        state.inquiriesReceived.error = false;
        state.inquiriesReceived.value = count;
        state.inquiriesReceived.variation = variation;
        state.inquiriesReceived.percentage_status = percentage_status;
        state.inquiriesReceived.icon = getArrowIcon(percentage_status);

        state.inquiriesReceived.options[INQUIRY_CATEGORIES.SOURCE].list = top;
        state.inquiriesReceived.options[INQUIRY_CATEGORIES.SOURCE].others =
          others;
        state.inquiriesReceived.options[INQUIRY_CATEGORIES.OPERATION].list =
          operation_breakdown;
      }
    );
    builder.addCase(getPerformanceReceivedLeadsCard.rejected, (state) => {
      state.inquiriesReceived.loading = false;
      state.inquiriesReceived.error = true;
    });

    builder.addCase(getPerformanceAddedPropertiesCard.pending, (state) => {
      state.addedProperties.loading = true;
      state.addedProperties.hasFetched = false;
      state.addedProperties.error = false;
    });
    builder.addCase(
      getPerformanceAddedPropertiesCard.fulfilled,
      (state, action) => {
        const {
          count,
          percentage_status,
          variation,
          operations,
          property_types,
        } = action.payload;

        const colors = ['#7B0C0D', '#ED3D3E', '#F16D6E', '#9AC1D0', '#427F94'];

        const typeLabels = property_types?.list?.map((type) => ({
          id: type.id,
          name: type.name,
          count: type.count,
          isOthers: false,
        }));
        const typeData = property_types?.list?.map((t) => t.count);

        if (property_types?.others?.length) {
          const othersCount = property_types.others.reduce(
            (acc, curr) => acc + curr.count,
            0
          );
          typeLabels.push({
            id: -1,
            name: 'Others',
            count: othersCount,
            isOthers: true,
          });
          typeData.push(othersCount);
        }

        const operationLabels = operations?.list?.map((operation) => ({
          id: operation.id,
          name: operation.name,
          count: operation.count,
          isOthers: false,
        }));
        const operationData = operations?.list?.map((o) => o.count);

        const makeDataset = (data) => [
          {
            label: Trans(translations, 'AddedProperties'),
            data,
            backgroundColor: colors.slice(0, data.length),
          },
        ];

        state.addedProperties.loading = false;

        state.addedProperties.hasFetched = true;
        state.addedProperties.error = false;
        state.addedProperties.value = count;
        state.addedProperties.variation = variation;
        state.addedProperties.percentage_status = percentage_status;
        state.addedProperties.icon = getArrowIcon(percentage_status);
        state.addedProperties.options = {
          [PROPERTY_ADDED_CATEGORIES.TYPE]: {
            labels: typeLabels,
            datasets: makeDataset(typeData),
          },
          [PROPERTY_ADDED_CATEGORIES.OPERATION]: {
            labels: operationLabels,
            datasets: makeDataset(operationData),
          },
        };
      }
    );
    builder.addCase(getPerformanceAddedPropertiesCard.rejected, (state) => {
      state.addedProperties.loading = false;
      state.addedProperties.error = true;
    });

    builder.addCase(getPerformanceDoneVisitsCard.pending, (state) => {
      state.doneVisits.loading = true;
      state.doneVisits.hasFetched = false;
      state.doneVisits.error = false;
    });
    builder.addCase(getPerformanceDoneVisitsCard.fulfilled, (state, action) => {
      const {
        count,
        variation,
        percentage_status,
        event_type_breakdown,
        closing_reason_breakdown,
        qualification_breakdown,
        without_closing_reason,
        without_qualification,
        not_closed,
      } = action.payload;

      const { top: visitTop, others: visitOthers } = event_type_breakdown ?? {
        top: [],
        others: [],
      };
      const { top: closingReasonTop, others: closingReasonOthers } =
        closing_reason_breakdown ?? { top: [], others: [] };

      state.doneVisits.loading = false;

      state.doneVisits.hasFetched = true;
      state.doneVisits.error = false;
      state.doneVisits.value = count;
      state.doneVisits.variation = variation;
      state.doneVisits.percentage_status = percentage_status;
      state.doneVisits.icon = getArrowIcon(percentage_status);

      state.doneVisits.options[DONE_VISITS.VISIT].list = visitTop;
      state.doneVisits.options[DONE_VISITS.VISIT].others = visitOthers;
      state.doneVisits.options[DONE_VISITS.CLOSING_REASON].list =
        closingReasonTop ?? [];
      state.doneVisits.options[DONE_VISITS.CLOSING_REASON].others =
        closingReasonOthers ?? [];
      state.doneVisits.options[DONE_VISITS.QUALIFICATION].list =
        qualification_breakdown ?? [];
      state.doneVisits.withoutClosingReason = without_closing_reason ?? {
        count: 0,
        percentage: 0,
      };
      state.doneVisits.withoutQualification = without_qualification ?? {
        count: 0,
        percentage: 0,
      };
      state.doneVisits.notClosed = not_closed ?? {
        count: 0,
        percentage: 0,
      };
    });
    builder.addCase(getPerformanceDoneVisitsCard.rejected, (state) => {
      state.doneVisits.loading = false;
      state.doneVisits.error = true;
    });
    builder.addCase(getPerformanceDoneEventsCard.pending, (state) => {
      state.doneEvents.loading = true;
      state.doneEvents.hasFetched = false;
      state.doneEvents.error = false;
    });
    builder.addCase(getPerformanceDoneEventsCard.fulfilled, (state, action) => {
      const {
        count,
        variation,
        percentage_status,
        event_type_breakdown,
        closing_reason_breakdown,
        qualification_breakdown,
        without_closing_reason,
        without_qualification,
        not_closed,
      } = action.payload;

      const { top: eventTop, others: eventOthers } = event_type_breakdown ?? {
        top: [],
        others: [],
      };
      const { top: closingReasonTop, others: closingReasonOthers } =
        closing_reason_breakdown ?? { top: [], others: [] };

      state.doneEvents.loading = false;

      state.doneEvents.hasFetched = true;
      state.doneEvents.error = false;
      state.doneEvents.value = count;
      state.doneEvents.variation = variation;
      state.doneEvents.percentage_status = percentage_status;
      state.doneEvents.icon = getArrowIcon(percentage_status);

      state.doneEvents.options[DONE_EVENTS.EVENT].list = eventTop;
      state.doneEvents.options[DONE_EVENTS.EVENT].others = eventOthers;
      state.doneEvents.options[DONE_EVENTS.CLOSING_REASON].list =
        closingReasonTop ?? [];
      state.doneEvents.options[DONE_EVENTS.CLOSING_REASON].others =
        closingReasonOthers ?? [];
      state.doneEvents.options[DONE_EVENTS.QUALIFICATION].list =
        qualification_breakdown ?? [];
      state.doneEvents.withoutClosingReason = without_closing_reason ?? {
        count: 0,
        percentage: 0,
      };
      state.doneEvents.withoutQualification = without_qualification ?? {
        count: 0,
        percentage: 0,
      };
      state.doneEvents.notClosed = not_closed ?? {
        count: 0,
        percentage: 0,
      };
    });
    builder.addCase(getPerformanceDoneEventsCard.rejected, (state) => {
      state.doneEvents.loading = false;
      state.doneEvents.error = true;
      state.doneEvents.options[DONE_EVENTS.EVENT].list = [];
      state.doneEvents.options[DONE_EVENTS.EVENT].others = [];
      state.doneEvents.options[DONE_EVENTS.CLOSING_REASON].list = [];
      state.doneEvents.options[DONE_EVENTS.CLOSING_REASON].others = [];
      state.doneEvents.options[DONE_EVENTS.QUALIFICATION].list = [];
      state.doneEvents.withoutClosingReason = { count: 0, percentage: 0 };
      state.doneEvents.withoutQualification = { count: 0, percentage: 0 };
      state.doneEvents.notClosed = { count: 0, percentage: 0 };
    });

    builder.addCase(getPerformanceNewContactsCard.pending, (state) => {
      state.newContacts.loading = true;
      state.newContacts.hasFetched = false;
      state.newContacts.error = false;
    });
    builder.addCase(
      getPerformanceNewContactsCard.fulfilled,
      (state, action) => {
        const { new_contacts, variation, percentage_status, periods } =
          action.payload;

        const labels = periods.map((item) => item.range);
        const datasets = [
          {
            label: Trans(translations, 'NewContacts'),
            data: periods.map((item) => item.new_contacts),
            pointRadius: 0,
          },
        ];

        state.newContacts.loading = false;

        state.newContacts.hasFetched = true;
        state.newContacts.error = false;
        state.newContacts.value = new_contacts;
        state.newContacts.variation = variation;
        state.newContacts.percentage_status = percentage_status;
        state.newContacts.icon = getArrowIcon(percentage_status);
        state.newContacts.labels = labels;
        state.newContacts.datasets = datasets;
      }
    );
    builder.addCase(getPerformanceNewContactsCard.rejected, (state) => {
      state.newContacts.loading = false;
      state.newContacts.error = true;
    });

    builder.addCase(getPerformanceConcretedOperationsCard.pending, (state) => {
      state.concretedOperations.loading = true;
      state.concretedOperations.hasFetched = false;
      state.concretedOperations.error = false;
    });
    builder.addCase(
      getPerformanceConcretedOperationsCard.fulfilled,
      (state, action) => {
        const { sale, rent, colleagues } = action.payload;

        const mapToChartData = (data) => {
          const labels = data.property_types.list.map((item) => item.name);
          const datasetData = data.property_types.list.map(
            (item) => item.count
          );
          const colors = [
            '#7B0000',
            '#E53935',
            '#FF6F61',
            '#90A4AE',
            '#546E7A',
          ];

          const datasets = [
            {
              label: Trans(translations, 'ConcretedOperations'),
              data: datasetData,
              backgroundColor: datasetData.map(
                (_, index) => colors[index % colors.length]
              ),
              borderWidth: 0,
              borderRadius: 4,
            },
          ];

          return {
            value: data.count,
            percentage_status: data.percentage_status,
            variation: data.variation,
            icon: getArrowIcon(data.percentage_status),
            labels,
            datasets,
          };
        };

        state.concretedOperations.loading = false;

        state.concretedOperations.hasFetched = true;
        state.concretedOperations.error = false;

        state.concretedOperations.options.sale = mapToChartData(sale);
        state.concretedOperations.options.rent = mapToChartData(rent);
        state.concretedOperations.options.colleagues =
          mapToChartData(colleagues);
      }
    );
    builder.addCase(getPerformanceConcretedOperationsCard.rejected, (state) => {
      state.concretedOperations.loading = false;
      state.concretedOperations.error = true;
    });

    builder.addCase(getPerformanceResponseTimeCard.pending, (state) => {
      state.responseTime.loading = true;
      state.responseTime.hasFetched = false;
      state.responseTime.error = false;
    });
    builder.addCase(
      getPerformanceResponseTimeCard.fulfilled,
      (state, action) => {
        const {
          min_time,
          max_time,
          percentage_status,
          median_time,
          variation,
          company_median_time = null,
        } = action.payload;

        state.responseTime.loading = false;

        state.responseTime.hasFetched = true;
        state.responseTime.error = false;
        state.responseTime.mdn = median_time;
        state.responseTime.max = max_time;
        state.responseTime.min = min_time;
        state.responseTime.company_median_time = company_median_time;
        state.responseTime.percentage_status = percentage_status;
        state.responseTime.variation = variation;
        state.responseTime.icon = getArrowIcon(percentage_status, false);
      }
    );
    builder.addCase(getPerformanceResponseTimeCard.rejected, (state) => {
      state.responseTime.loading = false;
      state.responseTime.error = true;
    });

    builder.addCase(getPerformanceConversionRateCard.pending, (state) => {
      state.conversionRate.loading = true;
      state.conversionRate.hasFetched = false;
      state.conversionRate.error = false;
    });
    builder.addCase(
      getPerformanceConversionRateCard.fulfilled,
      (state, action) => {
        const { rate, percentage_status, variation, funnel, losses } =
          action.payload;

        state.conversionRate.loading = false;

        state.conversionRate.hasFetched = true;
        state.conversionRate.error = false;
        state.conversionRate.rate = rate;
        state.conversionRate.percentage_status = percentage_status;
        state.conversionRate.variation = variation;
        state.conversionRate.icon = getArrowIcon(percentage_status);
        state.conversionRate.funnel = funnel;
        state.conversionRate.losses = losses;
      }
    );
    builder.addCase(getPerformanceConversionRateCard.rejected, (state) => {
      state.conversionRate.loading = false;
      state.conversionRate.error = true;
    });

    builder.addCase(getPerformanceConversionTimeCard.pending, (state) => {
      state.conversionTime.loading = true;
      state.conversionTime.hasFetched = false;
      state.conversionTime.error = false;
    });
    builder.addCase(
      getPerformanceConversionTimeCard.fulfilled,
      (state, action) => {
        const {
          min_time,
          max_time,
          percentage_status,
          median_time,
          variation,
        } = action.payload;

        state.conversionTime.loading = false;

        state.conversionTime.hasFetched = true;
        state.conversionTime.error = false;
        state.conversionTime.mdn = median_time;
        state.conversionTime.max = max_time;
        state.conversionTime.min = min_time;
        state.conversionTime.percentage_status = percentage_status;
        state.conversionTime.variation = variation;
        state.conversionTime.icon = getArrowIcon(percentage_status, false);
      }
    );
    builder.addCase(getPerformanceConversionTimeCard.rejected, (state) => {
      state.conversionTime.loading = false;
      state.conversionTime.error = true;
    });

    builder.addCase(getPerformanceClosedLeadsCard.pending, (state) => {
      state.closedLeads.loading = true;
      state.closedLeads.hasFetched = false;
      state.closedLeads.error = false;
    });
    builder.addCase(
      getPerformanceClosedLeadsCard.fulfilled,
      (state, action) => {
        const {
          count,
          variation,
          percentage_status,
          closing_reason_breakdown,
          qualification_breakdown,
        } = action.payload;

        state.closedLeads.loading = false;

        state.closedLeads.hasFetched = true;
        state.closedLeads.error = false;
        state.closedLeads.value = count;
        state.closedLeads.variation = variation;
        state.closedLeads.percentage_status = percentage_status;
        state.closedLeads.icon = getArrowIcon(percentage_status);

        state.closedLeads.options[CLOSED_LEADS.CLOSING_REASON].list =
          closing_reason_breakdown.top ?? [];
        state.closedLeads.options[CLOSED_LEADS.CLOSING_REASON].others =
          closing_reason_breakdown.others ?? [];
        state.closedLeads.options[CLOSED_LEADS.QUALIFICATION].list =
          qualification_breakdown ?? [];
      }
    );
    builder.addCase(getPerformanceClosedLeadsCard.rejected, (state) => {
      state.closedLeads.loading = false;
      state.closedLeads.error = true;
    });

    builder.addCase(getPerformanceReservationCard.pending, (state) => {
      state.reservationsCreated.loading = true;
      state.reservationsCreated.hasFetched = false;
      state.reservationsCreated.error = false;
    });
    builder.addCase(
      getPerformanceReservationCard.fulfilled,
      (state, action) => {
        const { count, percentage_status, variation, status_breakdown } =
          action.payload;

        const colors = ['#7B0C0D', '#ED3D3E', '#F16D6E', '#9AC1D0', '#427F94'];

        const labels = status_breakdown.map((item) => ({
          name: item.name,
          count: item.count,
        }));

        const data = status_breakdown.map((item) => item.count);
        const backgroundColors = status_breakdown.map(
          (_, index) => colors[index]
        );

        const datasets = [
          {
            label: Trans(translations, 'ReservationsCreated'),
            data,
            backgroundColor: backgroundColors,
          },
        ];

        state.reservationsCreated.loading = false;

        state.reservationsCreated.hasFetched = true;
        state.reservationsCreated.error = false;
        state.reservationsCreated.count = count;
        state.reservationsCreated.percentage_status = percentage_status;
        state.reservationsCreated.variation = variation;
        state.reservationsCreated.icon = getArrowIcon(percentage_status);
        state.reservationsCreated.labels = labels;
        state.reservationsCreated.datasets = datasets;
      }
    );
    builder.addCase(getPerformanceReservationCard.rejected, (state) => {
      state.reservationsCreated.loading = false;
      state.reservationsCreated.error = true;
    });

    builder.addCase(getPerformanceProductionRateCard.pending, (state) => {
      state.productionRate.loading = true;
      state.productionRate.hasFetched = false;
      state.productionRate.error = false;
    });
    builder.addCase(
      getPerformanceProductionRateCard.fulfilled,
      (state, action) => {
        const { rate, percentage_status, variation, funnel } = action.payload;

        state.productionRate.loading = false;

        state.productionRate.hasFetched = true;
        state.productionRate.error = false;
        state.productionRate.rate = rate;
        state.productionRate.percentage_status = percentage_status;
        state.productionRate.variation = variation;
        state.productionRate.icon = getArrowIcon(percentage_status);
        state.productionRate.funnel = funnel;
      }
    );
    builder.addCase(getPerformanceProductionRateCard.rejected, (state) => {
      state.productionRate.loading = false;
      state.productionRate.error = true;
    });

    builder.addCase(getPerformanceVisitEventsCard.pending, (state) => {
      state.doneVisitsEvolution.loading = true;
      state.doneVisitsEvolution.hasFetched = false;
      state.doneVisitsEvolution.error = false;
    });
    builder.addCase(
      getPerformanceVisitEventsCard.fulfilled,
      (state, action) => {
        const { visits, variation, percentage_status, periods } =
          action.payload;

        const labels = periods.map((item) => item.range);
        const datasets = [
          {
            label: Trans(translations, 'VisitsMadeEvolution'),
            data: periods.map((item) => item.visits),
            pointRadius: 0,
          },
        ];

        state.doneVisitsEvolution.loading = false;

        state.doneVisitsEvolution.hasFetched = true;
        state.doneVisitsEvolution.error = false;
        state.doneVisitsEvolution.value = visits;
        state.doneVisitsEvolution.variation = variation;
        state.doneVisitsEvolution.percentage_status = percentage_status;
        state.doneVisitsEvolution.icon = getArrowIcon(percentage_status);
        state.doneVisitsEvolution.labels = labels;
        state.doneVisitsEvolution.datasets = datasets;
      }
    );
    builder.addCase(getPerformanceVisitEventsCard.rejected, (state) => {
      state.doneVisitsEvolution.loading = false;
      state.doneVisitsEvolution.error = true;
    });

    builder.addCase(getPerformanceMedianResponseTimeCard.pending, (state) => {
      state.medianResponseTime.loading = true;
      state.medianResponseTime.hasFetched = false;
      state.medianResponseTime.error = false;
    });
    builder.addCase(
      getPerformanceMedianResponseTimeCard.fulfilled,
      (state, action) => {
        const { median_response_time, variation, percentage_status, periods } =
          action.payload;

        const labels = periods.map((item) => item.range);
        const datasets = [
          {
            label: Trans(translations, 'ResponseTimeEvolution'),
            data: periods.map((item) => item.median_response_time),
            pointRadius: 3,
            pointHoverRadius: 3,
          },
        ];

        state.medianResponseTime.loading = false;

        state.medianResponseTime.hasFetched = true;
        state.medianResponseTime.error = false;
        state.medianResponseTime.value = median_response_time ?? 0;
        state.medianResponseTime.variation = variation;
        state.medianResponseTime.percentage_status = percentage_status;
        state.medianResponseTime.icon = getArrowIcon(percentage_status, false);
        state.medianResponseTime.labels = labels;
        state.medianResponseTime.datasets = datasets;
      }
    );
    builder.addCase(getPerformanceMedianResponseTimeCard.rejected, (state) => {
      state.medianResponseTime.loading = false;
      state.medianResponseTime.error = true;
    });

    builder.addCase(
      getPerformancePropertyConsultationsCard.pending,
      (state) => {
        state.propertyConsultations.loading = true;
        state.propertyConsultations.hasFetched = false;
        state.propertyConsultations.error = false;
      }
    );
    builder.addCase(
      getPerformancePropertyConsultationsCard.fulfilled,
      (state, action) => {
        const { least_consulted = [], most_consulted = [] } = action.payload;

        state.propertyConsultations.loading = false;

        state.propertyConsultations.hasFetched = true;
        state.propertyConsultations.error = false;
        state.propertyConsultations.least_consulted = least_consulted.map(
          (property) => ({ ...property, type: property.property_type })
        );
        state.propertyConsultations.most_consulted = most_consulted.map(
          (property) => ({ ...property, type: property.property_type })
        );
      }
    );
    builder.addCase(
      getPerformancePropertyConsultationsCard.rejected,
      (state) => {
        state.propertyConsultations.loading = false;
        state.propertyConsultations.error = true;
      }
    );

    builder.addCase(
      getPerformanceDevelopmentConsultationsCard.pending,
      (state) => {
        state.developmentConsultations.loading = true;
        state.developmentConsultations.hasFetched = false;
        state.developmentConsultations.error = false;
      }
    );
    builder.addCase(
      getPerformanceDevelopmentConsultationsCard.fulfilled,
      (state, action) => {
        const { least_consulted = [], most_consulted = [] } = action.payload;

        state.developmentConsultations.loading = false;

        state.developmentConsultations.hasFetched = true;
        state.developmentConsultations.error = false;
        state.developmentConsultations.least_consulted = least_consulted.map(
          (development) => ({
            ...development,
            type: development.development_type,
          })
        );
        state.developmentConsultations.most_consulted = most_consulted.map(
          (development) => ({
            ...development,
            type: development.development_type,
          })
        );
      }
    );
    builder.addCase(
      getPerformanceDevelopmentConsultationsCard.rejected,
      (state) => {
        state.developmentConsultations.loading = false;
        state.developmentConsultations.error = true;
      }
    );

    builder.addCase(getPerformanceReceivedLeadsMiniCard.pending, (state) => {
      state.kpiCards.inquiriesReceived.loading = true;
      state.kpiCards.inquiriesReceived.hasFetched = false;
      state.kpiCards.inquiriesReceived.error = false;
    });
    builder.addCase(
      getPerformanceReceivedLeadsMiniCard.fulfilled,
      (state, action) => {
        const { count, percentage_status, variation } = action.payload;

        state.kpiCards.inquiriesReceived.loading = false;

        state.kpiCards.inquiriesReceived.hasFetched = true;
        state.kpiCards.inquiriesReceived.error = false;
        state.kpiCards.inquiriesReceived.value = count;
        state.kpiCards.inquiriesReceived.percentage_status = percentage_status;
        state.kpiCards.inquiriesReceived.variation = variation;
        state.kpiCards.inquiriesReceived.icon = getArrowIcon(percentage_status);
      }
    );
    builder.addCase(getPerformanceReceivedLeadsMiniCard.rejected, (state) => {
      state.kpiCards.inquiriesReceived.loading = false;
      state.kpiCards.inquiriesReceived.error = true;
    });

    builder.addCase(getPerformanceConversionRateMiniCard.pending, (state) => {
      state.kpiCards.conversionRate.loading = true;
      state.kpiCards.conversionRate.hasFetched = false;
      state.kpiCards.conversionRate.error = false;
    });
    builder.addCase(
      getPerformanceConversionRateMiniCard.fulfilled,
      (state, action) => {
        const { rate, percentage_status, variation } = action.payload;

        state.kpiCards.conversionRate.loading = false;

        state.kpiCards.conversionRate.hasFetched = true;
        state.kpiCards.conversionRate.error = false;
        state.kpiCards.conversionRate.value = rate;
        state.kpiCards.conversionRate.percentage_status = percentage_status;
        state.kpiCards.conversionRate.variation = variation;
        state.kpiCards.conversionRate.icon = getArrowIcon(percentage_status);
      }
    );
    builder.addCase(getPerformanceConversionRateMiniCard.rejected, (state) => {
      state.kpiCards.conversionRate.loading = false;
      state.kpiCards.conversionRate.error = true;
    });

    builder.addCase(getPerformanceNewContactsMiniCard.pending, (state) => {
      state.kpiCards.newContacts.loading = true;
      state.kpiCards.newContacts.hasFetched = false;
      state.kpiCards.newContacts.error = false;
    });
    builder.addCase(
      getPerformanceNewContactsMiniCard.fulfilled,
      (state, action) => {
        const { count, percentage_status, variation } = action.payload;

        state.kpiCards.newContacts.loading = false;

        state.kpiCards.newContacts.hasFetched = true;
        state.kpiCards.newContacts.error = false;
        state.kpiCards.newContacts.value = count;
        state.kpiCards.newContacts.percentage_status = percentage_status;
        state.kpiCards.newContacts.variation = variation;
        state.kpiCards.newContacts.icon = getArrowIcon(percentage_status);
      }
    );
    builder.addCase(getPerformanceNewContactsMiniCard.rejected, (state) => {
      state.kpiCards.newContacts.loading = false;
      state.kpiCards.newContacts.error = true;
    });

    builder.addCase(getPerformanceDoneEventsMiniCard.pending, (state) => {
      state.kpiCards.doneVisits.loading = true;
      state.kpiCards.doneVisits.hasFetched = false;
      state.kpiCards.doneVisits.error = false;
    });
    builder.addCase(
      getPerformanceDoneEventsMiniCard.fulfilled,
      (state, action) => {
        const { count, percentage_status, variation } = action.payload;

        state.kpiCards.doneVisits.loading = false;

        state.kpiCards.doneVisits.hasFetched = true;
        state.kpiCards.doneVisits.error = false;
        state.kpiCards.doneVisits.value = count;
        state.kpiCards.doneVisits.percentage_status = percentage_status;
        state.kpiCards.doneVisits.variation = variation;
        state.kpiCards.doneVisits.icon = getArrowIcon(percentage_status);
      }
    );
    builder.addCase(getPerformanceDoneEventsMiniCard.rejected, (state) => {
      state.kpiCards.doneVisits.loading = false;
      state.kpiCards.doneVisits.error = false;
    });

    builder.addCase(getPerformanceAddedPropertiesMiniCard.pending, (state) => {
      state.kpiCards.addedProperties.loading = true;
      state.kpiCards.addedProperties.hasFetched = false;
      state.kpiCards.addedProperties.error = false;
    });
    builder.addCase(
      getPerformanceAddedPropertiesMiniCard.fulfilled,
      (state, action) => {
        const { count, percentage_status, variation } = action.payload;

        state.kpiCards.addedProperties.loading = false;

        state.kpiCards.addedProperties.hasFetched = true;
        state.kpiCards.addedProperties.error = false;
        state.kpiCards.addedProperties.value = count;
        state.kpiCards.addedProperties.percentage_status = percentage_status;
        state.kpiCards.addedProperties.variation = variation;
        state.kpiCards.addedProperties.icon = getArrowIcon(percentage_status);
      }
    );
    builder.addCase(getPerformanceAddedPropertiesMiniCard.rejected, (state) => {
      state.kpiCards.addedProperties.loading = false;
      state.kpiCards.addedProperties.error = true;
    });

    builder.addCase(
      getPerformanceConcretedOperationsMiniCard.pending,
      (state) => {
        state.kpiCards.concretedOperations.loading = true;
        state.kpiCards.concretedOperations.hasFetched = false;
        state.kpiCards.concretedOperations.error = false;
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsMiniCard.fulfilled,
      (state, action) => {
        const { count, percentage_status, variation } = action.payload;

        state.kpiCards.concretedOperations.loading = false;

        state.kpiCards.concretedOperations.hasFetched = true;
        state.kpiCards.concretedOperations.error = false;
        state.kpiCards.concretedOperations.value = count;
        state.kpiCards.concretedOperations.percentage_status =
          percentage_status;
        state.kpiCards.concretedOperations.variation = variation;
        state.kpiCards.concretedOperations.icon =
          getArrowIcon(percentage_status);
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsMiniCard.rejected,
      (state) => {
        state.kpiCards.concretedOperations.loading = false;
        state.kpiCards.concretedOperations.error = true;
      }
    );

    builder.addCase(getPerformanceConversionTimeMiniCard.pending, (state) => {
      state.kpiCards.conversionTime.loading = true;
      state.kpiCards.conversionTime.hasFetched = false;
      state.kpiCards.conversionTime.error = false;
    });
    builder.addCase(
      getPerformanceConversionTimeMiniCard.fulfilled,
      (state, action) => {
        const { median_time, percentage_status, variation } = action.payload;

        state.kpiCards.conversionTime.loading = false;

        state.kpiCards.conversionTime.hasFetched = true;
        state.kpiCards.conversionTime.error = false;
        state.kpiCards.conversionTime.value = median_time ?? '-';
        state.kpiCards.conversionTime.percentage_status = percentage_status;
        state.kpiCards.conversionTime.variation = variation;
        state.kpiCards.conversionTime.icon = getArrowIcon(
          percentage_status,
          false
        );
      }
    );
    builder.addCase(getPerformanceConversionTimeMiniCard.rejected, (state) => {
      state.kpiCards.conversionTime.loading = false;
      state.kpiCards.conversionTime.error = true;
    });

    builder.addCase(getPerformanceResponseTimeMiniCard.pending, (state) => {
      state.kpiCards.responseTime.loading = true;
      state.kpiCards.responseTime.hasFetched = false;
      state.kpiCards.responseTime.error = false;
    });
    builder.addCase(
      getPerformanceResponseTimeMiniCard.fulfilled,
      (state, action) => {
        const { median_time, percentage_status, variation } = action.payload;

        state.kpiCards.responseTime.loading = false;

        state.kpiCards.responseTime.hasFetched = true;
        state.kpiCards.responseTime.error = false;
        state.kpiCards.responseTime.value = median_time ?? '-';
        state.kpiCards.responseTime.percentage_status = percentage_status;
        state.kpiCards.responseTime.variation = variation;
        state.kpiCards.responseTime.icon = getArrowIcon(
          percentage_status,
          false
        );
      }
    );
    builder.addCase(getPerformanceResponseTimeMiniCard.rejected, (state) => {
      state.kpiCards.responseTime.loading = false;
      state.kpiCards.responseTime.error = true;
    });

    builder.addCase(getPerformanceNewContactsTable.pending, (state, action) => {
      const modal = 'newContacts';
      const { tableQuery = {} } = action.meta.arg;

      DashboardSlice.caseReducers.setModalPendingState(state, {
        type: 'Dashboard/setModalPendingState',
        payload: { modal, tableQuery },
      });
    });
    builder.addCase(
      getPerformanceNewContactsTable.fulfilled,
      (state, action) => {
        const modal = 'newContacts';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceNewContactsTable.rejected, (state) => {
      state.performanceModals.newContacts.loading = false;
    });

    builder.addCase(
      getPerformanceInquiriesReceivedTable.pending,
      (state, action) => {
        const modal = 'inquiriesReceived';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceInquiriesReceivedTable.fulfilled,
      (state, action) => {
        const modal = 'inquiriesReceived';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceInquiriesReceivedTable.rejected, (state) => {
      state.performanceModals.inquiriesReceived.loading = false;
    });

    builder.addCase(getPerformanceDoneVisitsTable.pending, (state, action) => {
      const modal = 'doneVisits';
      const { tableQuery = {} } = action.meta.arg;

      DashboardSlice.caseReducers.setModalPendingState(state, {
        type: 'Dashboard/setModalPendingState',
        payload: { modal, tableQuery },
      });
    });
    builder.addCase(
      getPerformanceDoneVisitsTable.fulfilled,
      (state, action) => {
        const modal = 'doneVisits';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceDoneVisitsTable.rejected, (state) => {
      state.performanceModals.doneVisits.loading = false;
    });

    builder.addCase(getPerformanceDoneEventsTable.pending, (state, action) => {
      const modal = 'doneEvents';
      const { tableQuery = {} } = action.meta.arg;

      DashboardSlice.caseReducers.setModalPendingState(state, {
        type: 'Dashboard/setModalPendingState',
        payload: { modal, tableQuery },
      });
    });
    builder.addCase(
      getPerformanceDoneEventsTable.fulfilled,
      (state, action) => {
        const modal = 'doneEvents';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceDoneEventsTable.rejected, (state) => {
      state.performanceModals.doneEvents.loading = false;
    });

    builder.addCase(
      getPerformanceConversionTimeTable.pending,
      (state, action) => {
        const modal = 'conversionTime';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceConversionTimeTable.fulfilled,
      (state, action) => {
        const modal = 'conversionTime';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceConversionTimeTable.rejected, (state) => {
      state.performanceModals.conversionTime.loading = false;
    });

    builder.addCase(
      getPerformanceResponseTimeTable.pending,
      (state, action) => {
        const modal = 'responseTime';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceResponseTimeTable.fulfilled,
      (state, action) => {
        const modal = 'responseTime';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceResponseTimeTable.rejected, (state) => {
      state.performanceModals.responseTime.loading = false;
    });

    builder.addCase(
      getPerformanceAddedPropertiesTable.pending,
      (state, action) => {
        const modal = 'addedProperties';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceAddedPropertiesTable.fulfilled,
      (state, action) => {
        const modal = 'addedProperties';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceAddedPropertiesTable.rejected, (state) => {
      state.performanceModals.addedProperties.loading = false;
    });

    builder.addCase(
      getPerformanceConversionRateTable.pending,
      (state, action) => {
        const modal = 'conversionRate';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceConversionRateTable.fulfilled,
      (state, action) => {
        const modal = 'conversionRate';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceConversionRateTable.rejected, (state) => {
      state.performanceModals.conversionRate.loading = false;
    });

    builder.addCase(
      getPerformanceConcretedOperationsTable.pending,
      (state, action) => {
        const modal = 'concretedOperations';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsTable.fulfilled,
      (state, action) => {
        const modal = 'concretedOperations';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsTable.rejected,
      (state) => {
        state.performanceModals.concretedOperations.loading = false;
      }
    );

    builder.addCase(
      getPerformancePropertyConsultationsTable.pending,
      (state, action) => {
        const modal = 'propertyConsultations';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformancePropertyConsultationsTable.fulfilled,
      (state, action) => {
        const modal = 'propertyConsultations';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(
      getPerformancePropertyConsultationsTable.rejected,
      (state) => {
        state.performanceModals.propertyConsultations.loading = false;
      }
    );

    builder.addCase(
      getPerformanceDevelopmentConsultationsTable.pending,
      (state, action) => {
        const modal = 'developmentConsultations';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceDevelopmentConsultationsTable.fulfilled,
      (state, action) => {
        const modal = 'developmentConsultations';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(
      getPerformanceDevelopmentConsultationsTable.rejected,
      (state) => {
        state.performanceModals.developmentConsultations.loading = false;
      }
    );

    builder.addCase(getPerformanceClosedLeadsTable.pending, (state, action) => {
      const modal = 'closedLeads';
      const { tableQuery = {} } = action.meta.arg;

      DashboardSlice.caseReducers.setModalPendingState(state, {
        type: 'Dashboard/setModalPendingState',
        payload: { modal, tableQuery },
      });
    });
    builder.addCase(
      getPerformanceClosedLeadsTable.fulfilled,
      (state, action) => {
        const modal = 'closedLeads';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(getPerformanceClosedLeadsTable.rejected, (state) => {
      state.performanceModals.closedLeads.loading = false;
    });

    builder.addCase(
      getPerformanceReservationsCreatedTable.pending,
      (state, action) => {
        const modal = 'reservationsCreated';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );
    builder.addCase(
      getPerformanceReservationsCreatedTable.fulfilled,
      (state, action) => {
        const modal = 'reservationsCreated';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );
    builder.addCase(
      getPerformanceReservationsCreatedTable.rejected,
      (state) => {
        state.performanceModals.reservationsCreated.loading = false;
      }
    );

    builder.addCase(
      getPerformanceProductionRateTable.pending,
      (state, action) => {
        const modal = 'productionRate';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );

    builder.addCase(
      getPerformanceProductionRateTable.fulfilled,
      (state, action) => {
        const modal = 'productionRate';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: { ...action.payload, modal },
        });
      }
    );

    builder.addCase(getPerformanceProductionRateTable.rejected, (state) => {
      state.performanceModals.productionRate.loading = false;
    });

    builder.addCase(
      getPerformanceAgentsPerformanceTable.pending,
      (state, action) => {
        const modal = 'agentsPerformance';
        const { tableQuery = {} } = action.meta.arg;

        DashboardSlice.caseReducers.setModalPendingState(state, {
          type: 'Dashboard/setModalPendingState',
          payload: { modal, tableQuery },
        });
      }
    );

    builder.addCase(
      getPerformanceAgentsPerformanceTable.fulfilled,
      (state, action) => {
        const modal = 'agentsPerformance';

        DashboardSlice.caseReducers.setModalFulfilledState(state, {
          type: 'Dashboard/setModalFulfilledState',
          payload: {
            ...action.payload,
            modal,
            extraConfig: {
              skipPageUpdate: true,
              replaceResults: true,
            },
          },
        });
      }
    );
    builder.addCase(getPerformanceAgentsPerformanceTable.rejected, (state) => {
      state.performanceModals.agentsPerformance.loading = false;
    });

    builder.addCase(
      getPerformanceConcretedOperationsEvolutionCard.pending,
      (state) => {
        state.concretedOperationsEvolution.loading = true;
        state.concretedOperationsEvolution.hasFetched = false;
        state.concretedOperationsEvolution.error = false;
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsEvolutionCard.fulfilled,
      (state, action) => {
        const { count, variation, percentage_status, periods } = action.payload;

        const COLORS = {
          temporary: '#264856',
          sale: '#E95B41',
          rent: '#6D83E6',
        };

        const labels = periods.map((item) => item.range);
        const datasets = [
          {
            id: 'sale',
            label: Trans(translations, 'Sale'),
            data: periods.map((item) => item.sale),
            borderColor: COLORS.sale,
            pointBorderColor: COLORS.sale,
            pointBackgroundColor: COLORS.sale,
            pointHoverBackgroundColor: COLORS.sale,
            pointRadius: 3,
            pointHoverRadius: 3,
          },
          {
            id: 'rent',
            label: Trans(translations, 'Rent'),
            data: periods.map((item) => item.rent),
            borderColor: COLORS.rent,
            pointBorderColor: COLORS.rent,
            pointBackgroundColor: COLORS.rent,
            pointHoverBackgroundColor: COLORS.rent,
            pointRadius: 3,
            pointHoverRadius: 3,
          },
          {
            id: 'temporary',
            label: Trans(translations, 'Temporary'),
            data: periods.map((item) => item.temporary),
            borderColor: COLORS.temporary,
            pointBorderColor: COLORS.temporary,
            pointBackgroundColor: COLORS.temporary,
            pointHoverBackgroundColor: COLORS.temporary,
            pointRadius: 3,
            pointHoverRadius: 3,
          },
        ];

        state.concretedOperationsEvolution.loading = false;

        state.concretedOperationsEvolution.hasFetched = true;
        state.concretedOperationsEvolution.error = false;
        state.concretedOperationsEvolution.value = count;
        state.concretedOperationsEvolution.variation = variation;
        state.concretedOperationsEvolution.percentage_status =
          percentage_status;
        state.concretedOperationsEvolution.icon =
          getArrowIcon(percentage_status);
        state.concretedOperationsEvolution.labels = labels;
        state.concretedOperationsEvolution.datasets = datasets;
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsEvolutionCard.rejected,
      (state) => {
        state.concretedOperationsEvolution.loading = false;
        state.concretedOperationsEvolution.error = true;
      }
    );

    builder.addCase(
      getPerformanceConcretedOperationsSourceCard.pending,
      (state) => {
        state.concretedOperationsSource.loading = true;
        state.concretedOperationsSource.hasFetched = false;
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsSourceCard.fulfilled,
      (state, action) => {
        const { sale, rent } = action.payload;

        state.concretedOperationsSource.loading = false;

        state.concretedOperationsSource.hasFetched = true;
        state.concretedOperationsSource.sale = sale;
        state.concretedOperationsSource.rent = rent;
      }
    );
    builder.addCase(
      getPerformanceConcretedOperationsSourceCard.rejected,
      (state) => {
        state.concretedOperationsSource.loading = false;
      }
    );

    builder.addCase(putDashboardConfig.pending, (state) => {
      state.edition.loading = true;
    });
    builder.addCase(putDashboardConfig.fulfilled, (state) => {
      // reseting state to remove "dirty" keys
      const updatedTabs = buildFrontendStructureTabs(
        buildBackendStructureTabs(state.edition.tabs)
      );

      state.edition.loading = false;
      state.edition.active = false;
      state.edition.tabs = updatedTabs;

      // to force a refetching after save the config
      state.refreshCount += 1;
      // reset track entries
      state.edition.entriesToTrack = initialState.edition.entriesToTrack;
    });
    builder.addCase(putDashboardConfig.rejected, (state) => {
      state.edition.loading = false;
      state.edition.active = false;
      // to force a refetching
      state.refreshCount += 1;
    });
  },
});

export const {
  setTabActive,
  setMiniCards,
  setOverviewOperationStatistics,
  setPropertiesTypes,
  setPropertiesLocations,
  setConstructionAge,
  setEntryDate,
  setQprecoMarketplace,
  setInquiriesReceived,
  setAddedProperties,
  setConcretedOperations,
  setClosedLeads,
  setConcretedOperationsSource,
  setAllHasFetched,
  increaseRefreshCount,
  setPerformanceModals,
  clearPerformanceModalFilters,
  setEdition,
  setInitialEditionData,
  toggleEditionCardVisibility,
  toggleEditionTitleVisibility,
  toggleEditionCardExpand,
  moveEditionSection,
  reorderEditionCards,
  toggleActiveLegend,
  setOverviewTasks,
} = DashboardSlice.actions;

export default DashboardSlice.reducer;
